import React, { FC, useEffect, useMemo, useState } from "react";
import { Empty, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import ProjectPageShell from "../shared/ProjectPageShell";
import { AddAccess } from "../../../components/accessControl/AccessControl";
import { RootState } from "../../../store/store";
import { phaseApis } from "../../../api/Phase";
import {
  updateActivityInDetailModalSlice,
  resetActivityInDetailModalSlice,
} from "../../../store/slices/projectManagement/activity-view/ActivityInDetailView";
import ActivityCreation from "./ActivityCreation";
import { useWbsName } from "../shared/useWbsCodes";

interface PhaseOption {
  label: string;
  value: number;
  phaseStartDate?: string;
  phaseEndDate?: string;
}

// Full-page Add Activity form. Mirrors the Add Task page pattern: reads
// project/phase context from the URL + router state, dispatches the
// ActivityInDetailModal slice (so ActivityCreation reads context unchanged),
// then renders ActivityCreation in page mode with a sticky Save/Cancel footer.
const ActivityCreatePage: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const st: any = location.state || {};

  const { currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout
  );

  const projectId = Number(
    params.projectId || st.id || st.projectId || currentProjectId || 0
  );
  const projectName = st.projectName || currentProjectName || "Project";

  const [phaseOptions, setPhaseOptions] = useState<PhaseOption[]>([]);
  const wbsName = useWbsName();
  // Formatted at render so the labels follow the toggle without a refetch.
  const phaseSelectOptions = useMemo(
    () =>
      phaseOptions.map((option) => ({
        ...option,
        label: wbsName(option.label),
      })),
    [phaseOptions, wbsName]
  );
  const [selectedPhaseId, setSelectedPhaseId] = useState<number | null>(
    st.filterPhaseId ? Number(st.filterPhaseId) : null
  );
  const [ready, setReady] = useState(false);

  const selectedPhase = useMemo(
    () => phaseOptions.find((o) => o.value === selectedPhaseId) || null,
    [phaseOptions, selectedPhaseId]
  );

  // Load phases for the project (label + planned dates for date constraints).
  useEffect(() => {
    if (!projectId) return;
    phaseApis.fetchPhaseDropDown({ projectId }).then((res: any) => {
      if (res?.status && res?.data) {
        setPhaseOptions(
          res.data.map((item: any) => ({
            label: item.phaseName,
            value: item.phaseId,
            phaseStartDate: item.phaseStartDate,
            phaseEndDate: item.phaseEndDate,
          }))
        );
      }
    });
  }, [projectId]);

  // Push phase/project context into the Redux slice so ActivityCreation
  // resolves it exactly as it does in the drawer flow. Re-runs whenever the
  // selected phase changes.
  useEffect(() => {
    if (!projectId || !selectedPhase) {
      setReady(false);
      return;
    }
    dispatch(
      updateActivityInDetailModalSlice({
        open: true,
        id: null,
        activityName: "",
        projectResponse: {
          id: projectId,
          name: projectName,
          status: "Active",
        },
        phaseResponse: {
          id: selectedPhase.value,
          name: selectedPhase.label,
          status: "",
          startDate: selectedPhase.phaseStartDate || "",
          endDate: selectedPhase.phaseEndDate || "",
        },
        extra: {},
      })
    );
    setReady(true);
  }, [dispatch, projectId, projectName, selectedPhase]);

  // Clear slice on unmount so the drawer flow on other pages starts clean.
  useEffect(() => {
    return () => {
      dispatch(resetActivityInDetailModalSlice());
    };
  }, [dispatch]);

  const goToList = () => {
    dispatch(resetActivityInDetailModalSlice());
    navigate(`/pms/project/activities/${projectId}`, {
      state: { id: projectId, projectName },
    });
  };

  return (
    <ProjectPageShell
      headTitle="New Activity"
      breadcrumb={[
        { label: projectName },
        { label: "Activities", onClick: goToList },
        { label: "New Activity" },
      ]}
      onBack={goToList}
    >
      <AddAccess moduleName="Activities">
        <div className="pms-table-panel">
          <div className="activity-create-phase">
            <label>Phase</label>
            <Select
              placeholder="Select a phase"
              style={{ minWidth: 320 }}
              showSearch
              optionFilterProp="label"
              options={phaseSelectOptions}
              value={selectedPhaseId ?? undefined}
              onChange={(val) => setSelectedPhaseId(val ?? null)}
              filterOption={(input, option) =>
                ((option?.label as string) ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </div>

          {ready && selectedPhase ? (
            <ActivityCreation
              key={selectedPhase.value}
              mode="page"
              modalOpen={true}
              setModalOpen={() => {}}
              refreshData={() => {}}
              onCreated={goToList}
              onCancel={goToList}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Select a phase to start creating an activity"
              style={{ padding: "48px 0" }}
            />
          )}
        </div>
      </AddAccess>
    </ProjectPageShell>
  );
};

export default ActivityCreatePage;
